import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fetchOrder, fetchOrderAsset, SLOW_CONNECTION_MSG } from "@/lib/jsonp";
import CustomerPoSelector from "@/components/order/CustomerPoSelector";
import { customerPoSelection, isCustomerPoId, PO_SELECTION_ERROR } from "@/lib/customerPo";
import OrderSummaryCard from "@/components/order/OrderSummaryCard";
import OrderItemList from "@/components/order/OrderItemList";
import OrderTrackingDetails from "@/components/order/OrderTrackingDetails";

const REFRESH_MS = 60000;

export default function OrderLanding() {
    const location = useLocation();
    const navigate = useNavigate();
    const [wa, setWa] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [isConnectionError, setIsConnectionError] = useState(false);
    const [refreshDelayed, setRefreshDelayed] = useState(false);
    const [data, setData] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loadingMsg, setLoadingMsg] = useState("Menghubungkan pesanan...");
    const credsRef = useRef(null);
    const lastAttemptRef = useRef(null);
    const initialPoRef = useRef(undefined);
    const requestVersion = useRef(0);
    const inFlight = useRef(false);
    const orderController = useRef(null);
    const [trackingSession, setTrackingSession] = useState(0);
    const [selection, setSelection] = useState(null);
    const assetControllers = useRef(new Set());
    const assetEpoch = useRef(0);
    const clearAssets = useCallback(() => {
        assetEpoch.current += 1;
        assetControllers.current.forEach((controller) => controller.abort());
        assetControllers.current.clear();
    }, []);

    const load = useCallback(async (creds, { silent = false, keepSelection = false } = {}) => {
        if (inFlight.current) return;
        // Keep prior data only for a refresh of the same authenticated identity AND PO.
        const isRefresh = silent && credsRef.current?.code === creds.code &&
            (credsRef.current?.whatsapp || "") === (creds.whatsapp || "") &&
            (credsRef.current?.po_id || "") === (creds.po_id || "");
        inFlight.current = true;
        const controller = new AbortController();
        orderController.current = controller;
        const version = ++requestVersion.current;
        lastAttemptRef.current = creds;
        isRefresh ? setRefreshing(true) : setLoading(true);
        if (!isRefresh) {
            clearAssets();
            if (!keepSelection) setSelection(null);
            credsRef.current = null;
            setData(null);
            setUpdatedAt(null);
            setRefreshDelayed(false);
        }
        setLoadingMsg("Menghubungkan pesanan...");
        setError("");
        setIsConnectionError(false);
        try {
            if (creds.po_id !== undefined && !isCustomerPoId(creds.po_id)) {
                const invalidPo = new Error(PO_SELECTION_ERROR);
                invalidPo.authoritative = true;
                throw invalidPo;
            }
            const res = await fetchOrder(creds, {
                signal: controller.signal,
                onRetry: () => { if (version === requestVersion.current) setLoadingMsg("Mencoba menghubungkan kembali..."); },
            });
            if (version !== requestVersion.current) return;
            if (!res || !res.ok) {
                const responseError = new Error(res?.error || "Data tidak ditemukan.");
                responseError.authoritative = true;
                throw responseError;
            }
            const nextSelection = customerPoSelection(res, creds.po_id);
            if (!isRefresh) setTrackingSession((value) => value + 1);
            // Pin the resolved PO even when the initial request used the server default.
            const selectedCreds = { ...creds, ...(nextSelection.id ? { po_id: nextSelection.id } : {}) };
            credsRef.current = selectedCreds;
            lastAttemptRef.current = selectedCreds;
            setSelection(nextSelection);
            setData(res);
            const snapshotTime = res.snapshot_captured_at ? new Date(res.snapshot_captured_at) : new Date();
            setUpdatedAt(Number.isNaN(snapshotTime.getTime()) ? null : snapshotTime);
            setRefreshDelayed(false);
        } catch (e) {
            if (version !== requestVersion.current) return;
            if (!e.authoritative && e.message === SLOW_CONNECTION_MSG && isRefresh) {
                setRefreshDelayed(true);
            } else {
                clearAssets();
                setSelection(null);
                credsRef.current = null;
                setData(null);
                setUpdatedAt(null);
                setRefreshDelayed(false);
                setIsConnectionError(!e.authoritative && e.message === SLOW_CONNECTION_MSG);
                setError(e.message || "Terjadi kesalahan. Coba lagi.");
            }
        } finally {
            if (version === requestVersion.current) {
                inFlight.current = false;
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [clearAssets]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const c = params.get("code");
        if (c || params.has("po_id")) {
            // A PO-specific bill link remains bound to that PO through authentication.
            // Duplicate/blank/malformed explicit IDs are retained as invalid, never omitted.
            const values = params.getAll("po_id");
            initialPoRef.current = values.length === 0 ? undefined : values.length === 1 ? values[0] : "";
            // A new deep link can arrive while this route is still mounted.
            // Cancel/version the old identity before bypassing its in-flight guard.
            orderController.current?.abort();
            requestVersion.current += 1;
            inFlight.current = false;
            setRefreshing(false);
            if (c) {
                load({ code: c.trim(), ...(initialPoRef.current !== undefined ? { po_id: initialPoRef.current } : {}) });
            } else {
                clearAssets();
                credsRef.current = null;
                lastAttemptRef.current = null;
                setSelection(null);
                setData(null);
                setUpdatedAt(null);
                setRefreshDelayed(false);
                setLoading(false);
                setError("");
                setIsConnectionError(false);
            }
            window.history.replaceState(window.history.state, "", location.pathname);
        }
    }, [location.pathname, location.search, load, clearAssets]);

    useEffect(() => {
        if (!data) return;
        const id = setInterval(() => {
            if (document.visibilityState === "visible" && credsRef.current) load(credsRef.current, { silent: true });
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [data, load]);

    useEffect(() => () => { requestVersion.current += 1; orderController.current?.abort(); inFlight.current = false; clearAssets(); }, [clearAssets]);

    const assetScope = assetEpoch.current;
    const selectedPoId = data?.po?.id;
    const loadAsset = useCallback(async (asset, { signal, ...options } = {}) => {
        const creds = credsRef.current;
        if (!creds || !selectedPoId || creds.po_id !== selectedPoId || assetScope !== assetEpoch.current) {
            throw new DOMException("Sesi pesanan berakhir.", "AbortError");
        }
        const controller = new AbortController();
        const cancel = () => controller.abort();
        signal?.addEventListener("abort", cancel, { once: true });
        if (signal?.aborted) controller.abort();
        assetControllers.current.add(controller);
        try {
            const result = await fetchOrderAsset(creds, asset, { ...options, signal: controller.signal });
            if (controller.signal.aborted || assetScope !== assetEpoch.current) {
                throw new DOMException("Sesi pesanan berakhir.", "AbortError");
            }
            return result;
        } finally {
            signal?.removeEventListener("abort", cancel);
            assetControllers.current.delete(controller);
        }
    }, [assetScope, selectedPoId]);

    const selectPo = (poId) => {
        if (!selection?.options.some((po) => po.id === poId) || selection.id === poId) return;
        const creds = credsRef.current || lastAttemptRef.current;
        if (!creds) return;
        orderController.current?.abort();
        requestVersion.current += 1;
        inFlight.current = false;
        setRefreshing(false);
        setSelection((current) => ({ ...current, id: poId }));
        load({ ...creds, po_id: poId }, { keepSelection: true });
    };

    const submit = (e) => {
        e.preventDefault();
        if (!wa.trim() || !code.trim()) {
            setIsConnectionError(false);
            setError("Nomor WhatsApp dan Kode Akses wajib diisi.");
            return;
        }
        load({ whatsapp: wa.trim(), code: code.trim(), ...(initialPoRef.current !== undefined ? { po_id: initialPoRef.current } : {}) });
    };

    const reset = () => {
        clearAssets();
        setSelection(null);
        orderController.current?.abort();
        inFlight.current = false;
        requestVersion.current += 1;
        setTrackingSession((value) => value + 1);
        setLoading(false);
        setRefreshing(false);
        credsRef.current = null;
        lastAttemptRef.current = null;
        initialPoRef.current = undefined;
        setData(null);
        setUpdatedAt(null);
        setRefreshDelayed(false);
        setError("");
        setIsConnectionError(false);
        setCode("");
        navigate("/order", { replace: true });
    };

    const retry = () => {
        if (loading || refreshing) return;
        const creds = lastAttemptRef.current;
        if (creds) load(creds, { silent: !!data });
    };

    return (
        <div data-testid="page-order-landing" className="wx-container py-5 pb-10">
            {data || selection ? (
                <button
                    type="button"
                    data-testid="back-login"
                    onClick={reset}
                    className="inline-flex items-center gap-2 text-sm text-[color:var(--wx-muted)] hover:text-[color:var(--wx-ink)]"
                >
                    <ArrowLeft className="w-4 h-4" /> Keluar
                </button>
            ) : (
                <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm text-[color:var(--wx-muted)] hover:text-[color:var(--wx-ink)]">
                    <ArrowLeft className="w-4 h-4" /> Kembali
                </Link>
            )}

            {error && (
                <div data-testid="order-error" role={isConnectionError ? "status" : "alert"} className={`mt-4 rounded-xl border px-3.5 py-3 ${isConnectionError ? "border-[color:var(--wx-line)] bg-[color:var(--wx-surface)] text-[color:var(--wx-muted)]" : "border-red-200 bg-red-50 text-red-700"}`}>
                    <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
                        <div className="min-w-0">
                            <p className="text-[13px] font-semibold leading-tight">{isConnectionError ? "Pesanan belum terhubung" : "Pesanan belum dapat dimuat"}</p>
                            <p data-testid="order-error-detail" className="mt-0.5 text-[11px]">{isConnectionError ? "Data pesanan belum bisa dihubungi. Silakan coba lagi." : error}</p>
                            <p className="mt-1 text-[11px] leading-relaxed">
                                Silakan coba lagi. Jika masih belum terbuka, tutup halaman lalu buka kembali link pesanan dari WhatsApp.
                            </p>
                        </div>
                    </div>
                    {lastAttemptRef.current && (
                        <button
                            type="button"
                            data-testid="order-retry-btn"
                            onClick={retry}
                            disabled={loading || refreshing}
                            className={`mt-2.5 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white border text-[11px] font-semibold disabled:opacity-60 ${isConnectionError ? "border-[color:var(--wx-line)] text-[color:var(--wx-ink)] hover:bg-[color:var(--wx-surface)]" : "border-red-200 text-red-700 hover:bg-red-100"}`}
                        >
                            <RefreshCw className={`w-3 h-3 ${loading || refreshing ? "animate-spin" : ""}`} /> Coba Lagi
                        </button>
                    )}
                </div>
            )}

            {selection && <CustomerPoSelector options={selection.options} selectedId={selection.id} onSelect={selectPo} />}
            {selection && loading && (
                <div data-testid="order-po-loading" role="status" className="mt-4 wx-card p-5 flex items-center gap-2 text-xs text-[color:var(--wx-muted)]">
                    <Loader2 className="w-4 h-4 animate-spin text-[color:var(--wx-blue)]" /> Memuat pesanan PO yang dipilih...
                </div>
            )}
            {data ? (
                <div className="mt-4 wx-fade-up">
                    <OrderSummaryCard
                        data={data}
                        refreshing={refreshing}
                        updatedAt={updatedAt}
                        refreshDelayed={refreshDelayed}
                        onRefresh={() => credsRef.current && load(credsRef.current, { silent: true })}
                    />
                    <OrderItemList key={`items:${trackingSession}:${data.po?.id || ""}`} orders={data.orders} />
                    <OrderTrackingDetails
                        key={`tracking:${trackingSession}:${data.po?.id || ""}`}
                        reconciliation={data.payment_reconciliation}
                        summary={data.summary}
                        depositSummary={data.deposit_summary}
                        payments={data.payments}
                        paymentsAvailable={data.payments_available}
                        packages={data.packages}
                        refunds={data.refunds}
                        trackingAvailable={data.tracking_available}
                        contractMissing={typeof data.tracking_available !== "boolean"}
                        onRetry={retry}
                        onLoadAsset={loadAsset}
                    />
                </div>
            ) : selection && loading ? null : (
                <div className="mt-4 wx-card p-5">
                    <p className="wx-eyebrow">Pesanan Anda</p>
                    <h1 className="mt-1 text-lg font-semibold text-[color:var(--wx-ink)] tracking-tight">Cek Pesanan</h1>
                    <p className="mt-1 text-xs text-[color:var(--wx-muted)] leading-relaxed">
                        Masukkan nomor WhatsApp yang dipakai saat order dan kode akses yang dikirim admin WxFaous.
                    </p>

                    {loading && (
                        <div data-testid="order-loading" className="mt-4 flex items-center gap-2 text-xs text-[color:var(--wx-muted)]">
                            <Loader2 className="w-4 h-4 animate-spin text-[color:var(--wx-blue)]" /> {loadingMsg}
                        </div>
                    )}

                    <form onSubmit={submit} data-testid="order-login-form" className="mt-4 flex flex-col gap-2.5">
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-[color:var(--wx-ink-2)]">Nomor WhatsApp</span>
                            <Input
                                data-testid="order-wa-input"
                                value={wa}
                                onChange={(e) => setWa(e.target.value)}
                                inputMode="tel"
                                autoComplete="tel"
                                placeholder="0812xxxxxxx"
                                disabled={loading}
                                className="h-11 rounded-xl border-[color:var(--wx-line)] bg-white text-base"
                            />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-[color:var(--wx-ink-2)]">Kode Akses</span>
                            <Input
                                data-testid="order-code-input"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                autoComplete="off"
                                placeholder="WX........"
                                disabled={loading}
                                className="h-11 rounded-xl border-[color:var(--wx-line)] bg-white text-base uppercase"
                            />
                        </label>
                        <button
                            type="submit"
                            data-testid="order-login-submit"
                            disabled={loading}
                            className="wx-btn-primary mt-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Cek Pesanan <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}

